import { rem } from 'polished'
import React, { useContext } from 'react'
import styled from 'styled-components'

import CornerControls from './common/CornerControls'
import DetailCardLayout from './common/DetailCardLayout'
import ItemCardImage from './common/ItemCardImage'
import ItemTitle from './common/ItemTitle'
import NotesAndAttachments from './common/NotesAndAttachments'
import PrintOnlyAddressAndNotes from './common/PrintOnlyAddressAndNotes'
import { DetailItemCardVariant } from './common/types'
import ExperiencePriceAndCTAs from './ExperiencePriceAndCTAs'
import GooglePlaceRating from '../GooglePlaceRating/GooglePlaceRating'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import GeoContext from 'contexts/geoContext'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useExperience } from 'tripPlanner/hooks/api'
import { ItemMetadata } from 'tripPlanner/models'
import {
  AttractionItem,
  RestaurantBarItem,
  EventItem,
  ExperienceItem,
  ThingToDoItem,
} from 'tripPlanner/types/tripItem'
import { getDisplayableCustomThingToDoName } from 'tripPlanner/utils/itemConversions/common'

const CornerButtons = styled.div`
  float: right;
  padding-left: ${rem(8)};
  padding-bottom: ${rem(4)};
`

interface Props {
  item:
    | AttractionItem
    | RestaurantBarItem
    | EventItem
    | ThingToDoItem
    | ExperienceItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailThingToDoCard({ item, variant, tripNameLabel }: Props) {
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const viewAsTemplate = !!tripContext?.viewAsTemplate
  const Icon = ItemMetadata[item.type].icon

  const place = 'startPlace' in item ? item.startPlace : item.place
  const placeId = 'id' in place ? place.id : undefined

  const title =
    item.type === 'EXPERIENCE' ?
      item.name :
      getDisplayableCustomThingToDoName(item)

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const experienceId = item.type === 'EXPERIENCE' ? item.code : undefined
  const { data: experience } = useExperience({
    experienceId,
    currentCurrency,
    currentRegionCode,
  })

  let imgId: string | undefined = undefined
  let imgUrl: string | undefined = place.photoUrl

  if (item.type === 'EXPERIENCE') {
    imgId = item.savedItemData?.imageIds?.[0] ?? experience?.images[0]?.id
    imgUrl = experience?.images[0]?.url
  }

  return (
    <DetailCardLayout data-testid="detail-card-thing-to-do">
      <DetailCardLayout.Image>
        <ItemCardImage
          item={item}
          name={place.name}
          placeId={placeId}
          imgId={imgId}
          imgSrc={imgUrl}
        />
      </DetailCardLayout.Image>
      <DetailCardLayout.Top>
        <CornerButtons>
          {canEdit && <CornerControls item={item} variant={variant} />}
        </CornerButtons>
        <VerticalSpacer gap={4}>
          <BodyText variant="small" colour="neutral-two">
            {place.region}
          </BodyText>
          <ItemTitle icon={Icon} hideIconForMobile>
            {title}
          </ItemTitle>
          {placeId && <GooglePlaceRating placeId={placeId} size="S" />}
        </VerticalSpacer>
      </DetailCardLayout.Top>
      <DetailCardLayout.Bottom>
        <VerticalSpacer gap={12}>
          {tripNameLabel}
          <NotesAndAttachments
            item={item}
            canEdit={canEdit}
            variant={variant}
          />
          {item.type === 'EXPERIENCE' && experience && variant === 'drawer' && (
            <ExperiencePriceAndCTAs
              item={item}
              experience={experience}
              viewAsTemplate={viewAsTemplate}
              variant={variant}
            />
          )}
          <PrintOnlyAddressAndNotes item={item} />
        </VerticalSpacer>
      </DetailCardLayout.Bottom>
    </DetailCardLayout>
  )
}

export default DetailThingToDoCard
